<script setup lang="ts">
import { tailwindSurfaceFontClass } from '@@/bits/font'
import sectionSorter from '@@/bits/section_sorter'
import type { Group } from '@@/bits/surface_posts_grouping_helper'
import type { PostDragEvent } from '@@/bits/surface_sortable'
import { getSortableOptions } from '@@/bits/surface_sortable'
import { defineAsyncComponent } from '@@/bits/vue'
import { WallWishGroupByTypes } from '@@/enums'
import { OzDividerColorScheme } from '@@/library/v4/components/OzDivider.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfacePostsDragAndDropStore } from '@@/pinia/surface_posts_drag_and_drop'
import { useSurfaceSectionsStore } from '@@/pinia/surface_sections'
import { useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useSurfaceWishArrangementStore } from '@@/pinia/surface_wish_arrangement'
import type { Cid } from '@@/types'
import SurfaceMapPostListItem from '@@/vuecomponents/SurfaceMapPostListItem.vue'
import SurfacePostGroupHeaderSection from '@@/vuecomponents/SurfacePostGroupHeaderSection.vue'
import { usePostSorter } from '@@/vuecomposables/surface_post_sorter'
import { useSurfacePostsDragSortableMap } from '@@/vuecomposables/surface_posts_drag_sortable_map'
import { useSurfaceSectionsDragSortableMap } from '@@/vuecomposables/surface_sections_drag_sortable_map'
import { storeToRefs } from 'pinia'
import { computed, nextTick, ref, watch } from 'vue'
import Draggable from 'vuedraggable'

const OzDivider = defineAsyncComponent(() => import('@@/library/v4/components/OzDivider.vue'))
const SurfaceAddSectionButton = defineAsyncComponent(() => import('@@/vuecomponents/SurfaceAddSectionButton.vue'))

const props = withDefaults(
  defineProps<{
    isShown: boolean
  }>(),
  {
    isShown: false,
  },
)

const emit = defineEmits<{
  (name: 'start-reordering'): void
  (name: 'stop-reordering'): void
}>()

const surfaceMapStore = useSurfaceMapStore()
const surfacePostsStore = useSurfacePostsStore()
const surfaceSectionsStore = useSurfaceSectionsStore()

const { allSortedPostsByGroup } = storeToRefs(surfacePostsStore)
const { shouldDisableDraggingPost } = storeToRefs(useSurfacePostsDragAndDropStore())
const { sortedGroups, wishGroupBy } = storeToRefs(useSurfaceWishArrangementStore())
const { isASectionDragging } = storeToRefs(surfaceSectionsStore)
const { canIDragSections } = storeToRefs(useSurfacePermissionsStore())
const { colorScheme, fontId, isSectionBreakout, isSubmissionRequest, isFrozen } = storeToRefs(useSurfaceStore())
const { isDemoPadletPanelDesktop } = storeToRefs(useSurfaceOnboardingDemoPadletPanelStore())
const { isViewingTranslatedPadlet } = storeToRefs(useSurfaceTranslationsStore())

const { shouldDisplayPost } = surfacePostsStore

const fontClass = computed(() => tailwindSurfaceFontClass(fontId.value))

const internalPostsByGroup = ref(allSortedPostsByGroup.value)
const isDragging = ref(false)

const sortedGroupsToShow = computed(() => {
  if (isSubmissionRequest.value) return []
  return sortedGroups.value.map((group) => ({
    ...group,
    ariaLabelledby: `group-title-${group.id}`,
  }))
})

const shouldShowDivider = (group: Group, index: number) => {
  const isLastPostInGroup = index === (internalPostsByGroup.value[group.id] || []).length - 1
  if (isLastPostInGroup) return false
  return true
}

function forceSyncInternalPostsWithServer(): void {
  nextTick(() => {
    // Re-sync post array with server
    internalPostsByGroup.value = allSortedPostsByGroup.value
  })
}

watch(allSortedPostsByGroup, (newPostsByGroup) => {
  // `postsByGroup` can change in real-time when someone else adds or
  // deletes a post. Since we use an internal array to render the posts,
  // we need to sync it with `postsByGroup`. But this sync should only
  // happen when the user is not reordering the posts. Otherwise, drag
  // and drop will be messy.
  if (!isDragging.value) internalPostsByGroup.value = newPostsByGroup
})

/**
 * REORDERING SECTIONS
 */

// VueDraggable passes these options to SortableJS under the hood.
// For dragging sections in the vertical list, the default settings
// seem to create a better UX.
const sectionDraggableOptions = {
  ...getSortableOptions('section', 'map'),
  group: 'section-sortable-group',
  ghostClass: 'sortable-indicator-line',
  dragClass: 'sortable-dragging-clone',
  draggable: '.surface-section',
  handle: '.surface-group-title',
}

const { onSectionDragClone, onSectionDragStart, onSectionDragEnd } = useSurfaceSectionsDragSortableMap({
  onDragStart: () => {
    isASectionDragging.value = true
  },
  onDragEnd: (e) => {
    isASectionDragging.value = false
    const { groupId } = e
    if (groupId) {
      const sectionElement = document.querySelector(`#section-${groupId}`) as HTMLElement | undefined
      if (!sectionElement) return
      const sortResult = sectionSorter.getNewIndex(sectionElement)
      if (!sortResult) return
      surfaceSectionsStore.sortSection(sortResult)
    }
  },
})

/**
 * REORDERING POSTS
 */

// VueDraggable passes these options to SortableJS under the hood.
const postDraggableOptions = {
  ...getSortableOptions('post', 'map'),
  group: 'post-sortable-group',
}

const postSorter = usePostSorter()

function reorder({ postCid, groupId, newIndex }: { postCid: Cid; groupId: Group['id']; newIndex: number }): void {
  const postsInGroup = internalPostsByGroup.value[groupId] || []
  const prevPost = postsInGroup[newIndex - 1] || null
  const nextPost = postsInGroup[newIndex + 1] || null
  const { sortIndex } = postSorter.getNewIndexBetween(prevPost, nextPost)
  const sectionId =
    wishGroupBy.value === WallWishGroupByTypes.Section
      ? typeof groupId === 'string'
        ? parseInt(groupId)
        : groupId
      : null
  // Update new post order to server
  surfacePostsStore.sortPost({
    postCid,
    sortIndex,
    sectionId,
  })
}

const { onPostDragStart, onPostDragMove, onPostDragEnd } = useSurfacePostsDragSortableMap({
  onDragStart: () => {
    isDragging.value = true
    emit('start-reordering')
  },
  onDragEnd: (e: PostDragEvent) => {
    isDragging.value = false
    emit('stop-reordering')
    reorder({
      postCid: e.postCid!,
      groupId: e.groupId!,
      newIndex: e.newIndex!,
    })
    forceSyncInternalPostsWithServer()
  },
})

/**
 * HOVERING ON POSTS
 * We highlight the post marker when the user hovers over a post.
 */

function onPostHover(postCid: Cid): void {
  if (!isDragging.value) surfaceMapStore.highlightPostMarker({ postCid })
}

function onPostUnhover(): void {
  surfaceMapStore.highlightPostMarker({ postCid: null })
}

function onPostClick(postCid: Cid): void {
  surfaceMapStore.openPostPopup({ postCid })
  // auto focus on marker popup content if open post popup
  // we need the timeout of 100ms for the popup content to be populated in the popup
  setTimeout(() => {
    const googleMarkerPopup = document.querySelector('#surface-map-google-marker-popup-content')
    if (googleMarkerPopup != null) {
      const el = googleMarkerPopup as HTMLElement
      el.focus()
    }
  }, 100)
}

/**
 * We only flip neverShown to false for the first time
 * the post list is opened.
 */
const neverShown = ref(true)
const stopWatchingIsShown = watch(
  () => props.isShown,
  (isShown) => {
    if (neverShown.value && isShown) {
      neverShown.value = false
      setTimeout(() => stopWatchingIsShown(), 0)
    }
  },
  {
    immediate: true,
  },
)
</script>

<template>
  <section :aria-label="__('List of locations on this padlet')">
    <!-- N.B v-if is to load the panel for the first time, after that we use v-show so that
    whenever user open the panel, we don't have to load all the attachment previews
    again (which requires hitting display attributes endpoint for each post with attachment) -->
    <Draggable
      v-if="!neverShown"
      v-show="isShown"
      id="surface-map-post-list"
      :class="['flex flex-col', fontClass, isFrozen && 'pb-2.75']"
      :disabled="!canIDragSections"
      v-bind="sectionDraggableOptions"
      @clone="onSectionDragClone"
      @end="onSectionDragEnd"
      @start="onSectionDragStart"
    >
      <section
        v-for="group in sortedGroupsToShow"
        :id="`section-${group.id}`"
        :key="group.id"
        :data-id="group.id"
        :data-rank="group.sort_index"
        :class="{
          'surface-section': wishGroupBy === WallWishGroupByTypes.Section,
        }"
        :aria-labelledby="group.ariaLabelledby"
      >
        <!-- GROUP HEADER -->
        <template v-if="wishGroupBy === WallWishGroupByTypes.Section">
          <SurfacePostGroupHeaderSection :section="group" class="mx-3 mt-2" :use-default-bg-color="true" />
          <OzDivider
            :dark-mode="colorScheme === 'dark'"
            :color-scheme="OzDividerColorScheme.Solid"
            class="mx-3 surface-section-divider"
          />
        </template>

        <!-- POSTS IN GROUP -->
        <Draggable
          class="flex flex-col overflow-hidden select-none group-posts"
          :data-group-id="group.id"
          :list="internalPostsByGroup[group.id]"
          :disabled="shouldDisableDraggingPost || isASectionDragging"
          v-bind="postDraggableOptions"
          :move="onPostDragMove"
          @start="onPostDragStart"
          @end="onPostDragEnd"
        >
          <SurfaceMapPostListItem
            v-for="(post, index) in internalPostsByGroup[group.id]"
            v-show="shouldDisplayPost(post.cid)"
            :id="`wish-preview-${post.id}`"
            :key="post.cid"
            :font-class="fontClass"
            :post="post"
            :border-radius="0"
            :x-divider="shouldShowDivider(group, index)"
            @post-click="onPostClick"
            @post-hover="onPostHover"
            @post-unhover="onPostUnhover"
          />
        </Draggable>
      </section>

      <!-- ADD SECTION BUTTON -->
      <!-- On vertical list, the bottom padding should be 16px. -->
      <!-- Set a 8px bottom padding here so there's space for the focus ring. -->
      <!-- The other 8px bottom padding is set in SurfaceMapHeaderAndPostListPanel. -->
      <template #footer>
        <div
          v-if="
            !isASectionDragging &&
            !isSectionBreakout &&
            !isSubmissionRequest &&
            !isFrozen &&
            !isDemoPadletPanelDesktop &&
            !isViewingTranslatedPadlet
          "
          class="pb-2 w-fit"
        >
          <SurfaceAddSectionButton v-if="wishGroupBy === WallWishGroupByTypes.Section" class="ms-4 mt-2" />
        </div>
      </template>
    </Draggable>
  </section>
</template>

<style lang="scss" scoped>
.surface-section.sortable-persistent-clone {
  opacity: 0.5;
}

.surface-section.sortable-dragging-clone {
  opacity: 1 !important;
  transform: rotate(-2deg);
  box-shadow: none !important;
  filter: drop-shadow(0px 16px 32px rgba(0, 0, 0, 0.32));
}
</style>
